/**
 * Integrated Sustainability Tracking System
 * Combines Team A sustainability analytics with Team B real-time vehicle tracking
 * Features cross-component synchronization and live environmental monitoring
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Leaf,
  Truck,
  Zap,
  TrendingDown,
  MapPin,
  Clock,
  Battery,
  Fuel,
  Activity,
  Target,
  AlertTriangle,
  CheckCircle2,
  Gauge,
  BarChart3,
  Radio,
} from 'lucide-react';

// Team A Components
import { SustainabilityDashboard } from '../sustainability/SustainabilityDashboard';
import { BentoGrid, BentoGridItem } from '../common/BentoGrid';
import { MagicCard } from '../ui/magic/MagicCard';
import { NumberTicker } from '../ui/magic/NumberTicker';
import { AnimatedCircularProgressBar } from '../ui/magic/AnimatedCircularProgressBar';

import { cn } from '../../lib/utils';
import { useProject } from '../../contexts/ProjectContext';
import { useVehicles } from '../../hooks/useVehicles';

// Integration Types
interface RealTimeVehicleData {
  id: string;
  description: string;
  currentLocation: { lat: number; lng: number };
  speed: number; // km/h
  fuelLevel: number; // percentage
  batteryLevel?: number; // percentage for electric vehicles
  co2EmissionRate: number; // kg/h
  routeEfficiency: number; // percentage
  lastUpdate: Date;
  status: 'active' | 'idle' | 'charging' | 'maintenance';
}

interface LiveEnvironmentalMetrics {
  totalCO2Saved: number; // kg today
  realTimeFuelSavings: number; // liters today
  electricVehiclesActive: number;
  averageRouteEfficiency: number;
  carbonIntensity: number; // kg CO2 per km fleet average
  sustainabilityScore: number; // 0-100
  trending: 'improving' | 'stable' | 'declining';
}

interface SustainabilityTrackingIntegratedProps {
  className?: string;
  updateInterval?: number; // milliseconds
  showRealTimeData?: boolean;
  enableTeamBIntegration?: boolean;
}

// Mock real-time vehicle data (would come from Team B's tracking system)
const generateMockVehicleData = (vehicles: any[]): RealTimeVehicleData[] => {
  return vehicles.map(vehicle => ({
    id: vehicle.id.toString(),
    description: vehicle.description || `Vehicle ${vehicle.id}`,
    currentLocation: {
      lat: 40.7128 + (Math.random() - 0.5) * 0.1,
      lng: -74.0060 + (Math.random() - 0.5) * 0.1,
    },
    speed: Math.random() * 60 + 20, // 20-80 km/h
    fuelLevel: Math.random() * 100,
    batteryLevel: vehicle.description?.toLowerCase().includes('electric') ? Math.random() * 100 : undefined,
    co2EmissionRate: vehicle.description?.toLowerCase().includes('electric') ? 0 : Math.random() * 15 + 5,
    routeEfficiency: Math.random() * 30 + 70, // 70-100%
    lastUpdate: new Date(),
    status: Math.random() > 0.8 ? 'idle' : 'active',
  }));
};

export const SustainabilityTrackingIntegrated: React.FC<SustainabilityTrackingIntegratedProps> = ({
  className,
  updateInterval = 5000,
  showRealTimeData = true,
  enableTeamBIntegration = true,
}) => {
  const { currentProject } = useProject();
  const { data: vehicles = [], isLoading } = useVehicles(currentProject?.id || 0);

  // Real-time state management
  const [realTimeVehicles, setRealTimeVehicles] = useState<RealTimeVehicleData[]>([]);
  const [lastSyncTime, setLastSyncTime] = useState<Date>(new Date());
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connected');

  // Calculate live environmental metrics
  const liveMetrics = useMemo((): LiveEnvironmentalMetrics => {
    if (!realTimeVehicles.length) {
      return {
        totalCO2Saved: 0,
        realTimeFuelSavings: 0,
        electricVehiclesActive: 0,
        averageRouteEfficiency: 0,
        carbonIntensity: 0,
        sustainabilityScore: 0,
        trending: 'stable',
      };
    }

    const activeVehicles = realTimeVehicles.filter(v => v.status === 'active');
    const electricActive = activeVehicles.filter(v => v.batteryLevel !== undefined).length;
    
    // Calculate real-time CO2 savings (compared to diesel baseline)
    const baselineCO2Rate = 12; // kg/h for diesel vehicle
    const actualCO2Rate = activeVehicles.reduce((sum, v) => sum + v.co2EmissionRate, 0);
    const potentialCO2Rate = activeVehicles.length * baselineCO2Rate;
    const co2Saved = Math.max(0, potentialCO2Rate - actualCO2Rate) * 8; // 8 hours of operation
    
    // Calculate fuel savings
    const fuelSavingsRate = electricActive * 2.5; // 2.5L/h per electric vehicle
    const realTimeFuelSavings = fuelSavingsRate * 8;
    
    // Calculate average route efficiency
    const avgEfficiency = activeVehicles.reduce((sum, v) => sum + v.routeEfficiency, 0) / activeVehicles.length || 0;
    
    // Calculate carbon intensity
    const totalDistance = activeVehicles.reduce((sum, v) => sum + v.speed, 0);
    const carbonIntensity = totalDistance > 0 ? actualCO2Rate / totalDistance : 0;
    
    // Calculate sustainability score
    const electricRatio = electricActive / activeVehicles.length;
    const efficiencyScore = avgEfficiency / 100;
    const emissionScore = Math.max(0, 1 - (carbonIntensity / 0.3)); // 0.3 kg/km baseline
    const sustainabilityScore = Math.round(
      (electricRatio * 40 + efficiencyScore * 40 + emissionScore * 20) * 100
    );

    return {
      totalCO2Saved: Math.round(co2Saved),
      realTimeFuelSavings: Math.round(realTimeFuelSavings),
      electricVehiclesActive: electricActive,
      averageRouteEfficiency: Math.round(avgEfficiency),
      carbonIntensity: Number(carbonIntensity.toFixed(3)),
      sustainabilityScore,
      trending: sustainabilityScore > 75 ? 'improving' : sustainabilityScore > 50 ? 'stable' : 'declining',
    };
  }, [realTimeVehicles]);

  // Real-time data simulation (would connect to Team B's tracking system)
  useEffect(() => {
    if (!enableTeamBIntegration || !showRealTimeData || !vehicles.length) return;

    const updateData = () => {
      setConnectionStatus('connecting');
      
      // Simulate network delay
      setTimeout(() => {
        setRealTimeVehicles(generateMockVehicleData(vehicles));
        setLastSyncTime(new Date());
        setConnectionStatus('connected');
      }, 200 + Math.random() * 300);
    };

    // Initial load
    updateData();

    // Set up interval updates
    const interval = setInterval(updateData, updateInterval);

    return () => clearInterval(interval);
  }, [vehicles, updateInterval, enableTeamBIntegration, showRealTimeData]);

  const getTrendingIcon = (trending: LiveEnvironmentalMetrics['trending']) => {
    switch (trending) {
      case 'improving': return CheckCircle2;
      case 'declining': return AlertTriangle;
      default: return Gauge;
    }
  };

  const getTrendingColor = (trending: LiveEnvironmentalMetrics['trending']) => {
    switch (trending) {
      case 'improving': return 'text-green-600 bg-green-100';
      case 'declining': return 'text-red-600 bg-red-100';
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  if (isLoading) {
    return (
      <MagicCard className="p-8">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Connecting to Tracking Systems</h3>
            <p className="text-sm text-gray-600">Syncing Team A analytics with Team B real-time data...</p>
          </div>
        </div>
      </MagicCard>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header with Integration Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-green-100 to-blue-100">
            <Leaf className="w-8 h-8 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Integrated Sustainability Tracking</h2>
            <p className="text-gray-600">
              Real-time environmental monitoring • {currentProject?.name || 'All Projects'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Connection Status */}
          <motion.div
            className={cn(
              'flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium',
              connectionStatus === 'connected' ? 'bg-green-100 text-green-700' :
              connectionStatus === 'connecting' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            )}
            animate={connectionStatus === 'connecting' ? { scale: [1, 1.05, 1] } : {}}
            transition={{ duration: 1, repeat: connectionStatus === 'connecting' ? Infinity : 0 }}
          >
            <Radio className="w-4 h-4" />
            <span>
              {connectionStatus === 'connected' ? 'Live Data' :
               connectionStatus === 'connecting' ? 'Syncing...' : 'Disconnected'}
            </span>
          </motion.div>

          {/* Team Integration Indicator */}
          {enableTeamBIntegration && (
            <div className="flex items-center space-x-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
              <Activity className="w-4 h-4" />
              <span>Team A+B Integrated</span>
            </div>
          )}
        </div>
      </div>

      {/* Live Environmental Metrics */}
      <BentoGrid columns={{ sm: 2, md: 3, lg: 6 }} gap={4}>
        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <TrendingDown className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <NumberTicker
              value={liveMetrics.totalCO2Saved}
              suffix=" kg"
              className="text-xl font-bold text-green-600"
              duration={2.0}
            />
            <p className="text-xs text-gray-600">CO₂ Saved Today</p>
          </MagicCard>
        </BentoGridItem>

        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <Fuel className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <NumberTicker
              value={liveMetrics.realTimeFuelSavings}
              suffix=" L"
              className="text-xl font-bold text-blue-600"
              duration={2.2}
            />
            <p className="text-xs text-gray-600">Fuel Saved Today</p>
          </MagicCard>
        </BentoGridItem>

        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <Battery className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <NumberTicker
              value={liveMetrics.electricVehiclesActive}
              className="text-xl font-bold text-purple-600"
              duration={2.4}
            />
            <p className="text-xs text-gray-600">Electric Active</p>
          </MagicCard>
        </BentoGridItem>

        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <Target className="w-6 h-6 text-orange-600 mx-auto mb-2" />
            <NumberTicker
              value={liveMetrics.averageRouteEfficiency}
              suffix="%"
              className="text-xl font-bold text-orange-600"
              duration={2.6}
            />
            <p className="text-xs text-gray-600">Route Efficiency</p>
          </MagicCard>
        </BentoGridItem>

        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <BarChart3 className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
            <NumberTicker
              value={liveMetrics.sustainabilityScore}
              suffix="/100"
              className="text-xl font-bold text-indigo-600"
              duration={2.8}
            />
            <p className="text-xs text-gray-600">Live Score</p>
          </MagicCard>
        </BentoGridItem>

        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            {React.createElement(getTrendingIcon(liveMetrics.trending), {
              className: "w-6 h-6 mx-auto mb-2 " + getTrendingColor(liveMetrics.trending).split(' ')[0]
            })}
            <div className={cn(
              'text-sm font-bold uppercase',
              getTrendingColor(liveMetrics.trending).split(' ')[0]
            )}>
              {liveMetrics.trending}
            </div>
            <p className="text-xs text-gray-600">Trend</p>
          </MagicCard>
        </BentoGridItem>
      </BentoGrid>

      {/* Integration Layout */}
      <BentoGrid columns={{ sm: 1, md: 2 }} gap={6}>
        {/* Team A: Sustainability Dashboard */}
        <BentoGridItem className="md:col-span-1">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full" />
              <h3 className="text-lg font-semibold text-gray-900">Team A: Analytics Dashboard</h3>
            </div>
            
            <SustainabilityDashboard
              compactMode={true}
              showCalculator={false}
              timeframe="30d"
            />
          </div>
        </BentoGridItem>

        {/* Team B: Real-time Vehicle Tracking */}
        <BentoGridItem className="md:col-span-1">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-purple-500 rounded-full" />
              <h3 className="text-lg font-semibold text-gray-900">Team B: Live Vehicle Data</h3>
            </div>
            
            <MagicCard className="p-4">
              <div className="space-y-3">
                {realTimeVehicles.slice(0, 5).map(vehicle => (
                  <div key={vehicle.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={cn(
                        'p-2 rounded-lg',
                        vehicle.batteryLevel !== undefined ? 'bg-green-100' : 'bg-blue-100'
                      )}>
                        {vehicle.batteryLevel !== undefined ? (
                          <Battery className="w-4 h-4 text-green-600" />
                        ) : (
                          <Fuel className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-gray-900">{vehicle.description}</h4>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>Speed: {Math.round(vehicle.speed)} km/h</span>
                          <span>Efficiency: {Math.round(vehicle.routeEfficiency)}%</span>
                          <span>CO₂: {vehicle.co2EmissionRate.toFixed(1)} kg/h</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className={cn(
                      'px-2 py-1 rounded-full text-xs font-medium',
                      vehicle.status === 'active' ? 'bg-green-100 text-green-700' :
                      vehicle.status === 'charging' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    )}>
                      {vehicle.status}
                    </div>
                  </div>
                ))}
              </div>
            </MagicCard>
          </div>
        </BentoGridItem>
      </BentoGrid>

      {/* Cross-Component Sync Status */}
      <MagicCard className="p-4 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              <span>Team A: Historical analytics and sustainability calculations</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-purple-500 rounded-full" />
              <span>Team B: Real-time vehicle tracking and live environmental data</span>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Clock className="w-4 h-4" />
            <span>Last sync: {lastSyncTime.toLocaleTimeString()}</span>
          </div>
        </div>
      </MagicCard>
    </div>
  );
};